import { get, set } from 'idb-keyval';
import { getSettings } from './storage.js';

const KEY = 'trackside-scanner/activity-v1';
const MAX_ENTRIES = 120;

/**
 * @typedef {'register_ok'|'register_dup'|'verify_ok'|'verify_fail'|'limit_season'|'limit_batch'|'error'|'session'} ActivityKind
 */

/**
 * @param {object} partial
 * @param {ActivityKind} partial.kind
 * @param {number} [partial.startNumber]
 * @param {string} [partial.wheel]
 * @param {string} [partial.detail]
 * @param {{ apiBase?: string, seriesName?: string }} [sync]
 */
export async function appendActivity(partial, sync) {
  let list = [];
  try {
    list = (await get(KEY)) || [];
  }
  catch {
    list = [];
  }

  const { value: settings = {} } = await getSettings();
  const operator = typeof settings.operatorLabel === 'string' ? settings.operatorLabel.trim() : '';

  const entry = {
    id: `${ Date.now() }-${ Math.random().toString(36).slice(2, 10) }`,
    ts: Date.now(),
    operator,
    ...partial
  };

  list.unshift(entry);

  if (list.length > MAX_ENTRIES) {
    list.length = MAX_ENTRIES;
  }

  try {
    await set(KEY, list);
  }
  catch {
    /* non-fatal */
  }

  if (sync?.apiBase && sync?.seriesName) {
    void pushActivityToServer(sync.apiBase, sync.seriesName, entry);
  }

  return entry;
}

export async function getActivity() {
  try {
    return (await get(KEY)) || [];
  }
  catch {
    return [];
  }
}

/**
 * @param {string} apiBase
 * @param {string} seriesName
 * @returns {Promise<{ ok: true, entries: object[] } | { ok: false }>}
 */
export async function fetchTyreActivityFromApi(apiBase, seriesName) {
  if (!apiBase || !seriesName) {
    return { ok: false };
  }

  const url = `${ apiBase }/tyre-activity?series=${ encodeURIComponent(seriesName) }`;

  try {
    const response = await fetch(url, {
      headers: { Accept: 'application/json' },
      cache: 'no-store'
    });

    if (response.status === 404) {
      return { ok: false };
    }

    if (!response.ok) {
      return { ok: false };
    }

    const data = await response.json();
    const raw  = Array.isArray(data)
      ? data
      : data?.entries ?? data?.items ?? data?.activity ?? data?.data;

    if (!Array.isArray(raw)) {
      return { ok: true, entries: [] };
    }

    const entries = raw.map(normalizeServerActivityRow).filter(Boolean);
    entries.sort((a, b) => (b.ts || 0) - (a.ts || 0));

    return { ok: true, entries };
  }
  catch {
    return { ok: false };
  }
}

/**
 * @param {Record<string, unknown>} row
 */
function normalizeServerActivityRow(row) {
  if (!row || typeof row !== 'object') {
    return null;
  }

  let ts = typeof row.ts === 'number' ? row.ts : NaN;

  if (!Number.isFinite(ts)) {
    const iso = row.createdAt ?? row.at ?? row.time;

    if (typeof iso === 'string' || typeof iso === 'number') {
      const n = Date.parse(String(iso));
      ts        = Number.isFinite(n) ? n : Date.now();
    }
    else {
      ts = Date.now();
    }
  }

  const startRaw = row.startNumber ?? row.start ?? row.car ?? row.sn;

  let startNumber;

  if (typeof startRaw === 'number' && Number.isFinite(startRaw)) {
    startNumber = startRaw;
  }
  else if (startRaw != null && String(startRaw).trim() !== '') {
    const n = Number.parseInt(String(startRaw).trim(), 10);
    startNumber = Number.isFinite(n) ? n : undefined;
  }

  const kind = row.kind ?? row.type ?? row.event ?? 'error';
  const id   = typeof row.id === 'string' ? row.id : String(row.id ?? `${ ts }-${ kind }`);

  return {
    id,
    ts,
    kind,
    startNumber,
    wheel:    typeof row.wheel === 'string' ? row.wheel : row.wheel != null ? String(row.wheel) : '',
    detail:   typeof row.detail === 'string' ? row.detail : row.detail != null ? String(row.detail) : '',
    operator: typeof row.operator === 'string' ? row.operator : row.operator != null ? String(row.operator) : ''
  };
}

/**
 * @param {string} apiBase
 * @param {string} seriesName
 * @param {object} entry
 */
async function pushActivityToServer(apiBase, seriesName, entry) {
  const url = `${ apiBase }/tyre-activity`;

  try {
    await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept:         'application/json'
      },
      body: JSON.stringify({
        series: seriesName,
        ...entry
      })
    });
  }
  catch {
    /* non-fatal: local log still holds the event */
  }
}

function startOfLocalDay(ts) {
  const d = new Date(ts);
  d.setHours(0, 0, 0, 0);
  return d.getTime();
}

function isTyreEvent(kind) {
  return kind === 'register_ok' || kind === 'register_dup' || kind === 'verify_ok' || kind === 'verify_fail';
}

function isViolationKind(kind) {
  return kind === 'verify_fail' || kind === 'limit_season' || kind === 'limit_batch';
}

function formatTime(ts) {
  return new Date(ts).toLocaleString('sv-SE', {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
}

function kindLabel(kind) {
  switch (kind) {
    case 'register_ok':
      return 'Registered';
    case 'register_dup':
      return 'Duplicate scan';
    case 'verify_ok':
      return 'Verified';
    case 'verify_fail':
      return 'Infringement';
    case 'limit_season':
      return 'Season limit';
    case 'limit_batch':
      return 'Batch limit';
    case 'error':
      return 'Error';
    case 'session':
      return 'Session';
    default:
      return kind;
  }
}

/**
 * @param {HTMLElement} root
 * @param {{ apiBase?: string, seriesName?: string }} [options]
 */
export async function updateHomeDashboard(root, options = {}) {
  if (!root) {
    return;
  }

  const { apiBase, seriesName } = options;
  let entries                   = await getActivity();
  let source                    = 'device';

  if (apiBase && seriesName) {
    const remote = await fetchTyreActivityFromApi(apiBase, seriesName);

    if (remote.ok) {
      entries = remote.entries;
      source  = 'server';
    }
  }

  const dayStart = startOfLocalDay(Date.now());
  let todayTyre  = 0;
  let todayViol = 0;

  for (const e of entries) {
    if (e.ts < dayStart) {
      continue;
    }

    if (isTyreEvent(e.kind)) {
      todayTyre += 1;
    }

    if (isViolationKind(e.kind)) {
      todayViol += 1;
    }
  }

  const violationCount = todayViol;
  const violations = entries.filter(e => isViolationKind(e.kind)).slice(0, 6);
  const recent = entries.slice(0, 10);

  const statToday = root.querySelector('[data-dash-stat-today]');
  const statViol = root.querySelector('[data-dash-stat-violations]');

  if (statToday) {
    statToday.textContent = String(todayTyre);
  }

  if (statViol) {
    statViol.textContent = String(violationCount);
  }

  const ulRecent = root.querySelector('[data-dash-recent]');
  const ulViol   = root.querySelector('[data-dash-violations]');
  const noteEl   = root.querySelector('[data-dash-source-note]');

  if (noteEl) {
    noteEl.innerHTML = source === 'server'
      ? 'Figures below include <strong>all operators</strong> whose devices sync to the trackside service for this series.'
      : 'Showing <strong>this device only</strong> — connect the trackside tyre-activity API to see fleet-wide data.';
  }

  const emptyRecent = source === 'server'
    ? '<li class="dash-list__empty">No tyre events logged for this series yet.</li>'
    : '<li class="dash-list__empty">No tyre events logged yet on this device.</li>';
  const emptyViol = source === 'server'
    ? '<li class="dash-list__empty">No violations recorded for this series yet.</li>'
    : '<li class="dash-list__empty">No violations logged on this device.</li>';

  if (ulRecent) {
    ulRecent.innerHTML = recent.length
      ? recent.map(e => activityRow(e)).join('')
      : emptyRecent;
  }

  if (ulViol) {
    ulViol.innerHTML = violations.length
      ? violations.map(e => activityRow(e)).join('')
      : emptyViol;
  }
}

function activityRow(e) {
  const who = e.operator ? `${ escapeHtml(e.operator) } · ` : '';
  const sn  = e.startNumber ? `#${ e.startNumber }` : '';
  const wh  = e.wheel ? ` ${ escapeHtml(e.wheel) }` : '';
  const det = e.detail ? ` — ${ escapeHtml(e.detail) }` : '';
  const label = kindLabel(e.kind);

  return `<li class="dash-list__row">
    <span class="dash-list__time">${ formatTime(e.ts) }</span>
    <span class="dash-list__body"><strong>${ escapeHtml(label) }</strong> ${ who }${ sn }${ wh }${ det }</span>
  </li>`;
}

function escapeHtml(s) {
  return String(s)
    .replace(/&/gu, '&amp;')
    .replace(/</gu, '&lt;')
    .replace(/>/gu, '&gt;')
    .replace(/"/gu, '&quot;');
}

/** Map API position id suffix to short corner label. */
export function tyrePositionToShort(pos) {
  const m = {
    frontLeft: 'LF',
    frontRight: 'RF',
    rearLeft: 'LR',
    rearRight: 'RR'
  };

  return m[pos] || pos || '';
}

/**
 * @param {string} message
 */
export function kindFromScanErrorMessage(message) {
  const m = String(message || '');

  if (m.includes('24') && m.toLowerCase().includes('record')) {
    return 'limit_season';
  }

  if (m.toLowerCase().includes('batch') || m.includes('10')) {
    return 'limit_batch';
  }

  return 'error';
}
